import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { mq, fontScale } from '@/theme/scalingTokens';

/**
 * Inline filter editor styles.
 *
 * Strategy:
 * - Desktop (≥900px): single-line flex row with proportional widths.
 * - Tablet/smallDesktop (≤900px): 2-column grid wrap so fields stay readable.
 * - Mobile (≤480px): single-column stack with natural Fluent control heights.
 *
 * On desktop we flatten the Fluent `<Field>` via `display: contents` into a
 * fixed-height 2-row grid (label / control) to keep every heterogeneous control
 * (Combobox, daterange, timerange, SpinButton) on the same baseline.
 * Below smallDesktop the fixed-grid override is dropped so labels can wrap
 * and date/time sub-pickers can stack naturally.
 */
export const useInlineFilterEditorStyles = makeStyles({
  root: {
    // Desktop: fields grid + button row sit on a single horizontal line.
    // Below smallDesktop the breakpoint below flips this back to a column.
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    columnGap: '0.85rem',
    rowGap: '0.85rem',
    ...shorthands.padding('0.6rem', '0.85rem'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('0.375rem'),
    fontSize: fontScale.caption,

    // ---- DESKTOP: force uniform 2-row grid + display:contents Field ----
    '& > div > div[class]': {
      display: 'grid',
      gridTemplateRows: 'auto auto',
      gridTemplateColumns: 'minmax(0, 1fr)',
      rowGap: '0.125rem',
      alignItems: 'stretch',
      minWidth: 0,
    },
    '& .fui-Field': {
      display: 'contents',
    },
    '& .fui-Field__label, & label.fui-Label': {
      gridRow: 1,
      fontSize: fontScale.caption,
      lineHeight: 1.4,
      margin: 0,
      padding: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'block',
    },
    '& .fui-Combobox, & .fui-Dropdown, & .fui-Input, & .fui-SpinButton': {
      gridRow: 2,
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    },

    // ---- date-range / time-range wrapper (sibling to label) ----
    '& .fui-Field > div:has(> span)': {
      gridRow: 2,
      display: 'flex',
      flexWrap: 'nowrap',
      columnGap: '0.25rem',
      alignItems: 'center',
      minWidth: 0,
    },
    '& .fui-Field > div:has(> span) > *:not(span)': {
      flex: '1 1 0',
      minWidth: 0,
    },
    '& .fui-Field > div:has(> span) > span': {
      fontSize: fontScale.caption,
      flexShrink: 0,
      ...shorthands.padding('0', '0.125rem'),
    },

    // ---- Inner input text sizing (desktop default) ----
    '& .fui-Combobox input, & .fui-Dropdown button': {
      fontSize: fontScale.caption,
      minWidth: 0,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    '& .fui-Input input, & .fui-SpinButton input': {
      fontSize: fontScale.caption,
      minWidth: 0,
    },
    '& .fui-Field > div:has(> span) .fui-Input input': {
      fontSize: fontScale.caption,
      minWidth: 0,
      textOverflow: 'clip',
    },

    // ---- Chevron visibility ----
    '& .fui-Combobox > *:not(input), & .fui-Dropdown > *:not(button)': {
      flexShrink: 0,
    },
    '& .fui-Combobox svg, & .fui-Dropdown svg, & .fui-Combobox [class*="expandIcon"], & .fui-Dropdown [class*="expandIcon"]':
      {
        flexShrink: 0,
        display: 'inline-block',
        visibility: 'visible',
        opacity: 1,
      },

    // ---- Validation messages: visually-hidden but a11y-accessible ----
    '& .fui-Field__validationMessage, & .fui-FieldMessage': {
      position: 'absolute',
      width: '1px',
      height: '1px',
      ...shorthands.padding('0'),
      ...shorthands.margin('-1px'),
      ...shorthands.overflow('hidden'),
      clip: 'rect(0, 0, 0, 0)',
      clipPath: 'inset(50%)',
      whiteSpace: 'nowrap',
      ...shorthands.border('0'),
    },

    // ====================================================================
    // TABLET / SMALL DESKTOP (≤900px): relax fixed sizing, allow wrapping
    // ====================================================================
    [mq.smallDesktop]: {
      // Below smallDesktop fields stack vertically again so the button row
      // can drop to its own line.
      flexDirection: 'column',
      alignItems: 'stretch',
      fontSize: fontScale.body,
      '& > div > div[class]': {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '0.25rem',
      },
      '& .fui-Field__label, & label.fui-Label': {
        whiteSpace: 'normal',
        overflow: 'visible',
      },
      // Restore native Fluent layout for the Field — no grid contents
      '& .fui-Field': {
        display: 'block',
      },
      // Show validation messages inline on smaller screens
      '& .fui-Field__validationMessage, & .fui-FieldMessage': {
        position: 'static',
        width: 'auto',
        height: 'auto',
        clip: 'auto',
        clipPath: 'none',
        whiteSpace: 'normal',
        margin: 0,
        overflow: 'visible',
        fontSize: fontScale.caption,
      },
    },

    // ====================================================================
    // MOBILE (≤480px): stack date/time sub-pickers vertically
    // ====================================================================
    [mq.mobile]: {
      gap: '0.75rem',
      ...shorthands.padding('0.75rem'),
      '& .fui-Field > div:has(> span)': {
        flexDirection: 'column',
        alignItems: 'stretch',
        rowGap: '0.25rem',
      },
      '& .fui-Field > div:has(> span) > *:not(span)': {
        width: '100%',
      },
      '& .fui-Field > div:has(> span) > span': {
        display: 'none',
      },
    },
  },

  fieldsGrid: {
    // Take all space not consumed by the button row.
    flex: '1 1 auto',
    minWidth: 0,
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '0.5rem',
    alignItems: 'stretch',
    '& > *': {
      minWidth: 0,
    },
    // Tightened ratios so the inline editor leaves room for Update/Cancel
    // on the same line on desktop.
    '& > *:nth-child(1)': { flex: '1 1 0' },
    '& > *:nth-child(2)': { flex: '1 1 0' },
    '& > *:nth-child(3)': { flex: '2.4 1 0' },
    '& > *:nth-child(4)': { flex: '1.4 1 0' },
    '& > *:nth-child(5)': { flex: '0.5 1 0' },
    [mq.smallDesktop]: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '0.75rem',
      '& > *': {
        flex: 'unset',
      },
    },
    [mq.mobile]: {
      gridTemplateColumns: '1fr',
    },
  },
  buttonRow: {
    // Sits inline with fieldsGrid on desktop; consumes only the space it needs.
    flex: '0 0 auto',
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '0.5rem',
    flexWrap: 'nowrap',
    [mq.smallDesktop]: {
      flex: '0 0 auto',
      flexWrap: 'wrap',
    },
    [mq.mobile]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      '& > *': {
        width: '100%',
      },
    },
  },
});
